import { readImageFileAsDataUrl } from "@/character/frame";
import { HOME_CELL_PX } from "@/home/grid";
import { imageDataUrlForStorage } from "@/home/image-data-url";
import { mountKindPicker, type KindPickerHandle } from "@/home/kind-picker";
import {
  mountHomeSheetCrop,
  type HomePartDraft,
  type HomeSheetCropHandle,
} from "@/home/mount-sheet-crop";
import { normalizePartKind, type HomePartKind } from "@/home/part-kind";
import {
  applyAutoBackgroundRemovalFromDataUrl,
  bindAutoRemoveBgCheckbox,
  homeAutoRemoveBgToggleMarkup,
} from "@/home/remove-white-bg";
import { clampPartCells } from "@/home/storage";

export type PartUploadFlowOptions = {
  allowedKinds: HomePartKind[];
  defaultKind?: HomePartKind;
  onCreate: (drafts: HomePartDraft[]) => void | Promise<void>;
  createButtonLabel?: string;
};

export type PartUploadFlowHandle = {
  destroy: () => void;
  reset: () => void;
};

function defaultPartNameFromFile(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, "").trim();
  return base.slice(0, 24) || "ชิ้น";
}

function suggestCellsFromImage(width: number, height: number): { cellsW: number; cellsH: number } {
  return {
    cellsW: clampPartCells(Math.ceil(width / HOME_CELL_PX)),
    cellsH: clampPartCells(Math.ceil(height / HOME_CELL_PX)),
  };
}

export function mountPartUploadFlow(
  host: HTMLElement,
  options: PartUploadFlowOptions,
): PartUploadFlowHandle {
  const allowedKinds = options.allowedKinds;
  const defaultKind = options.defaultKind ?? allowedKinds[0] ?? "solid";

  host.innerHTML = `
    <div class="home-part-upload-flow">
      <div id="part-flow-single-root">
        <div id="part-single-upload-step" class="home-part-upload-step">
          <label class="btn btn-primary sheet-upload btn-block home-part-upload-cta">
            เลือกรูปจากเครื่อง
            <input type="file" id="part-flow-image" accept="image/png,image/jpeg,image/webp" hidden />
          </label>
          <p class="hint home-part-upload-lede">เลือกรูปก่อน · แล้วเลือกชิ้นเดียวหรือหลายชิ้น</p>
          ${homeAutoRemoveBgToggleMarkup({
            id: "part-flow-auto-remove-bg",
            extraClass: "home-part-bg-auto",
          })}
          <p id="part-flow-error" class="form-error" role="alert" hidden></p>
        </div>
        <div id="part-single-after-upload" class="home-part-after-upload" hidden>
          <div class="home-part-upload-preview-wrap">
            <img id="part-flow-preview" class="home-part-upload-preview" alt="" width="96" height="96" />
            <button type="button" id="part-flow-change-image" class="btn btn-ghost btn-sm">เปลี่ยนรูป</button>
          </div>
          <div id="part-single-mode-step" class="home-part-mode-step">
            <p class="hint home-part-mode-lede">รูปนี้เป็นชิ้นเดียว หรือแผ่นที่มีหลายชิ้น?</p>
            <div class="home-part-mode-choices">
              <button type="button" id="part-flow-mode-single" class="btn btn-primary home-part-mode-btn">ชิ้นเดียว</button>
              <button type="button" id="part-flow-mode-multi" class="btn btn-ghost home-part-mode-btn">หลายชิ้น</button>
            </div>
            <p class="hint">หลายชิ้น → ครอปกริด / แยกอัตโนมัติ · ตั้งชื่อและหมวดต่อชิ้น</p>
          </div>
          <div id="part-single-config-step" class="home-part-config-step" hidden>
            <label class="field">
              <span class="field-label">ชื่อ</span>
              <input type="text" id="part-flow-name" class="text-input" maxlength="80" placeholder="เช่น โต๊ะ" />
            </label>
            <div class="field home-part-kind-field">
              <span class="field-label">หมวด</span>
              <div id="part-flow-kind-host"></div>
            </div>
            <div class="home-part-size-row">
              <label class="field field--inline">
                <span class="field-label">กว้าง (ช่อง)</span>
                <input type="number" id="part-flow-w" class="text-input" min="1" max="4" value="1" />
              </label>
              <label class="field field--inline">
                <span class="field-label">สูง (ช่อง)</span>
                <input type="number" id="part-flow-h" class="text-input" min="1" max="4" value="1" />
              </label>
            </div>
            <button type="button" id="part-flow-add-single" class="btn btn-primary btn-block">เพิ่มชิ้นนี้</button>
          </div>
        </div>
      </div>
      <div id="part-flow-sheet-host" class="home-sheet-crop-host gm-part-sheet-host" hidden></div>
    </div>
  `;

  let pendingImage: string | null = null;
  let pendingOriginal: string | null = null;
  let pendingFileName = "";
  let partSingleMode: "single" | null = null;
  let kindPicker: KindPickerHandle | undefined;
  let sheetCrop: HomeSheetCropHandle | undefined;

  const singleRoot = host.querySelector<HTMLElement>("#part-flow-single-root");
  const sheetHost = host.querySelector<HTMLElement>("#part-flow-sheet-host");
  const errorEl = host.querySelector<HTMLElement>("#part-flow-error");

  const showError = (message: string) => {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  };

  const hideError = () => {
    errorEl?.setAttribute("hidden", "true");
  };

  const prepareUploadDataUrl = async (raw: string): Promise<string> => {
    return applyAutoBackgroundRemovalFromDataUrl(raw);
  };

  const syncSteps = () => {
    const hasImage = Boolean(pendingImage);
    const uploadStep = host.querySelector<HTMLElement>("#part-single-upload-step");
    const afterUpload = host.querySelector<HTMLElement>("#part-single-after-upload");
    const modeStep = host.querySelector<HTMLElement>("#part-single-mode-step");
    const configStep = host.querySelector<HTMLElement>("#part-single-config-step");
    if (uploadStep) uploadStep.hidden = hasImage;
    if (afterUpload) afterUpload.hidden = !hasImage;
    if (modeStep) modeStep.hidden = !hasImage || partSingleMode !== null;
    if (configStep) configStep.hidden = !hasImage || partSingleMode !== "single";
    const preview = host.querySelector<HTMLImageElement>("#part-flow-preview");
    if (preview) {
      if (hasImage && pendingImage) {
        preview.src = pendingImage;
        preview.hidden = false;
      } else {
        preview.removeAttribute("src");
        preview.hidden = true;
      }
    }
  };

  const reset = () => {
    pendingImage = null;
    pendingOriginal = null;
    pendingFileName = "";
    partSingleMode = null;
    hideError();
    if (singleRoot) singleRoot.hidden = false;
    if (sheetHost) sheetHost.hidden = true;
    sheetCrop?.resetWizard();
    const nameInput = host.querySelector<HTMLInputElement>("#part-flow-name");
    const wInput = host.querySelector<HTMLInputElement>("#part-flow-w");
    const hInput = host.querySelector<HTMLInputElement>("#part-flow-h");
    const fileInput = host.querySelector<HTMLInputElement>("#part-flow-image");
    if (nameInput) nameInput.value = "";
    if (wInput) wInput.value = "1";
    if (hInput) hInput.value = "1";
    kindPicker?.setValue(defaultKind);
    if (fileInput) fileInput.value = "";
    syncSteps();
  };

  const mountKindPickerOnce = () => {
    if (kindPicker) return;
    const kindHost = host.querySelector<HTMLElement>("#part-flow-kind-host");
    if (!kindHost) return;
    kindPicker = mountKindPicker(defaultKind, { id: "part-flow-kind", kinds: allowedKinds });
    kindHost.replaceChildren(kindPicker.root);
  };

  const onDraftsCreated = async (drafts: HomePartDraft[]) => {
    if (drafts.length === 0) return;
    await options.onCreate(drafts);
    reset();
  };

  if (sheetHost) {
    sheetCrop = mountHomeSheetCrop(sheetHost, (drafts) => void onDraftsCreated(drafts), {
      allowedKinds,
      defaultAutoSplitKind: defaultKind,
      createButtonLabel: options.createButtonLabel ?? "เพิ่มชิ้นที่เลือก",
    });
  }

  const autoRemoveBgInput = host.querySelector<HTMLInputElement>("#part-flow-auto-remove-bg");
  if (autoRemoveBgInput) bindAutoRemoveBgCheckbox(autoRemoveBgInput);

  const onRemoveBgChanged = () => {
    void (async () => {
      if (!pendingOriginal) return;
      try {
        pendingImage = await prepareUploadDataUrl(pendingOriginal);
        syncSteps();
      } catch {
        /* keep previous */
      }
    })();
  };
  window.addEventListener("home-remove-white-bg-changed", onRemoveBgChanged);
  window.addEventListener("home-remove-black-bg-changed", onRemoveBgChanged);

  host.querySelector<HTMLInputElement>("#part-flow-image")?.addEventListener("change", () => {
    const input = host.querySelector<HTMLInputElement>("#part-flow-image");
    const file = input?.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        const dataUrl = await readImageFileAsDataUrl(file);
        pendingOriginal = dataUrl;
        pendingImage = await prepareUploadDataUrl(dataUrl);
        pendingFileName = file.name;
        partSingleMode = null;
        hideError();
        syncSteps();
        const size = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => reject(new Error("อ่านขนาดรูปไม่ได้"));
          img.src = pendingImage!;
        });
        const nameInput = host.querySelector<HTMLInputElement>("#part-flow-name");
        const wInput = host.querySelector<HTMLInputElement>("#part-flow-w");
        const hInput = host.querySelector<HTMLInputElement>("#part-flow-h");
        const suggested = suggestCellsFromImage(size.width, size.height);
        if (nameInput) nameInput.value = defaultPartNameFromFile(file.name);
        if (wInput) wInput.value = String(suggested.cellsW);
        if (hInput) hInput.value = String(suggested.cellsH);
        mountKindPickerOnce();
        kindPicker?.setValue(defaultKind);
      } catch (err) {
        pendingImage = null;
        pendingOriginal = null;
        pendingFileName = "";
        partSingleMode = null;
        syncSteps();
        showError(err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ");
        if (input) input.value = "";
      }
    })();
  });

  host.querySelector("#part-flow-change-image")?.addEventListener("click", () => {
    reset();
    requestAnimationFrame(() => host.querySelector<HTMLInputElement>("#part-flow-image")?.click());
  });

  host.querySelector("#part-flow-mode-single")?.addEventListener("click", () => {
    if (!pendingImage) return;
    partSingleMode = "single";
    hideError();
    mountKindPickerOnce();
    syncSteps();
    requestAnimationFrame(() => host.querySelector<HTMLInputElement>("#part-flow-name")?.focus());
  });

  host.querySelector("#part-flow-mode-multi")?.addEventListener("click", () => {
    if (!pendingImage || !sheetCrop) return;
    const dataUrl = pendingOriginal ?? pendingImage;
    const label = pendingFileName || "ภาพ";
    void (async () => {
      try {
        if (singleRoot) singleRoot.hidden = true;
        if (sheetHost) sheetHost.hidden = false;
        await sheetCrop.loadSourceImage(dataUrl, label, { startStep: "crop" });
      } catch (err) {
        if (singleRoot) singleRoot.hidden = false;
        if (sheetHost) sheetHost.hidden = true;
        showError(err instanceof Error ? err.message : "โหลดภาพไม่สำเร็จ");
        syncSteps();
      }
    })();
  });

  host.querySelector("#part-flow-add-single")?.addEventListener("click", () => {
    const name = host.querySelector<HTMLInputElement>("#part-flow-name")?.value.trim() ?? "";
    const cellsW = clampPartCells(Number(host.querySelector<HTMLInputElement>("#part-flow-w")?.value ?? 1));
    const cellsH = clampPartCells(Number(host.querySelector<HTMLInputElement>("#part-flow-h")?.value ?? 1));
    const kind = kindPicker?.getValue() ?? defaultKind;
    if (!name) {
      showError("ใส่ชื่อชิ้นส่วน");
      return;
    }
    if (!pendingImage) {
      showError("อัปโหลดภาพก่อน");
      return;
    }
    void onDraftsCreated([
      {
        name,
        imageDataUrl: imageDataUrlForStorage(pendingImage),
        cellsW,
        cellsH,
        kind: normalizePartKind(kind),
      },
    ]);
  });

  syncSteps();

  return {
    destroy: () => {
      window.removeEventListener("home-remove-white-bg-changed", onRemoveBgChanged);
      window.removeEventListener("home-remove-black-bg-changed", onRemoveBgChanged);
      sheetCrop?.destroy();
      host.replaceChildren();
    },
    reset,
  };
}
